import { useMemo } from 'react';
import { Platform } from 'react-native';
import { usePathname } from 'expo-router';
import { CHAT_INPUT_STYLE, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS, getEffectiveContextSidebarWidth, getNavigationSidebarWidth } from '@/constants/config';
import { getPetById } from '@/constants/pets';
import { getDesktopPetComposerClearance, isDesktopPetOccupyingComposer } from '@/constants/utils/desktopPetLayout';
import { isArtifactSidebarType } from '@/constants/utils/files';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useAuthStore, useUIStore, useVoiceModeStore } from '@/store';
import { useArtifactViewerStore } from '@/store/artifactViewer';

export function useDesktopPetComposerLayout({
  chatInputStyle,
  existingRightPadding = 0,
} = {}) {
  const pathname = usePathname();
  const { isMD, windowWidth } = useResponsiveness();
  const selectedPetId = useAuthStore(state => state.user?.preferences?.appearance?.selectedPet || null);
  const isVoiceModeActive = useVoiceModeStore(state => state.isActive);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const isArtifactViewerOpen = useArtifactViewerStore(state => state.isOpen);
  const navigationSidebarWidth = useUIStore(state => getNavigationSidebarWidth(state, windowWidth));
  const contextSidebarWidth = useUIStore(state => (
    state.isContextSidebarVisible ? getEffectiveContextSidebarWidth(state, windowWidth) : 0
  ));
  const messageWidth = useUIStore(state => state.messageWidth);
  const preferredInputStyle = useUIStore(state => state.chatInputStyle);
  const effectiveInputStyle = chatInputStyle || preferredInputStyle;
  const isArtifactOpen = isArtifactViewerOpen
    || (isContextSidebarVisible && isArtifactSidebarType(contextSidebarType));
  const isDesktop = Platform.OS === 'web' || Platform.OS === 'macos';
  const hasPet = Boolean(getPetById(selectedPetId));

  const isVisible = isDesktopPetOccupyingComposer({
    pathname,
    isDesktop,
    isMD,
    hasPet,
    isVoiceModeActive,
    isArtifactOpen,
  });

  const composerMaxWidth = useMemo(() => {
    if (effectiveInputStyle === CHAT_INPUT_STYLE.FLOATING) {
      if (messageWidth === MESSAGE_WIDTH.NARROW) return MESSAGE_WIDTH_AMOUNTS.NARROW;
      if (messageWidth === MESSAGE_WIDTH.MEDIUM) return MESSAGE_WIDTH_AMOUNTS.MEDIUM;
    }
    return Number.POSITIVE_INFINITY;
  }, [effectiveInputStyle, messageWidth]);

  const clearance = getDesktopPetComposerClearance({
    isVisible,
    windowWidth,
    navigationSidebarWidth,
    contextSidebarWidth,
    composerMaxWidth,
    existingRightPadding,
  });
  // On Mac, hide the pet as soon as its natural gutter disappears. This
  // updates on resize and sidebar changes without narrowing the composer.
  const visible = isVisible && !(Platform.OS === 'macos' && clearance > 0);
  return { isVisible: visible, clearance: !visible || effectiveInputStyle === CHAT_INPUT_STYLE.DETAILED ? 0 : clearance };
}

export default function useDesktopPetComposerClearance(options) {
  return useDesktopPetComposerLayout(options).clearance;
}
